import React, { useEffect, useState } from "react";
import { 
  Box, 
  Container, 
  Typography, 
  Button,
  Paper,
  CircularProgress,
  Alert
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../../firebase";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../../firebase";
import { FamilyMember } from "../../../utils/familyTreeUtils";
import { 
  GenerationalChain, 
  generateAvailableChains, 
  prepareGenerationalArtForOrder,
  GenerationalArtOrder
} from "../../../utils/generationalArtUtils";
import ChainSelectionPanel from "../../../components/generationalArt/ChainSelectionPanel";
import ChainPreviewPanel from "../../../components/generationalArt/ChainPreviewPanel";
import OrderConfigurationActions from "../../../components/generationalArt/OrderConfigurationActions";
import { checkAndUpdateOrderProgress } from "../../../utils/firebaseUtils";

const BG_IMAGE = "https://media.rainpos.com/663/hex5f021f_burgundy_design_swirl_boldtiny09867633333333.jpg";

const GenerationalStudioPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("orderId");
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState<GenerationalArtOrder | null>(null);
  const [familyTreeData, setFamilyTreeData] = useState<FamilyMember[]>([]);
  const [availableChains, setAvailableChains] = useState<GenerationalChain[]>([]);
  
  // Configuration State
  const [selectedChainId, setSelectedChainId] = useState<string>("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!orderId || !user) return;

      try {
        setLoading(true);
        
        // 1. Fetch Order
        const orderSnap = await getDoc(doc(db, "orders", orderId));
        if (orderSnap.exists()) {
          const orderData = orderSnap.data();
          
          // Construct minimal order object for our utilities
          const currentOrder: GenerationalArtOrder = {
              paypalOrderId: orderSnap.id,
              items: orderData.items || [],
              status: orderData.status,
              actionsRequired: orderData.actionsRequired || [],
              formData: orderData.formData || {},
              createdAt: orderData.createdAt,
              maxGenerations: orderData.items?.[0]?.selectedOptions?.generations || 4, // Default or fetch from options
              isCompleted: !!orderData.formStatus?.Generational
          };
          
          setOrder(currentOrder);

          // Restore previous selection if exists
          if (orderData.formData?.Generational?.chainId) {
             setSelectedChainId(orderData.formData.Generational.chainId);
          }
        }

        // 2. Fetch User's Family Tree
        const userSnap = await getDoc(doc(db, "users", user.uid));
        if (userSnap.exists()) {
           const userData = userSnap.data();
           const treeData = userData.familyTreeData || [];
           setFamilyTreeData(treeData);
           
           // 3. Generate Available Chains
           const chains = generateAvailableChains(treeData);
           setAvailableChains(chains);
        }

      } catch (error) {
        console.error("Error loading studio data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [orderId, user]);

  const handleSave = async () => {
      if (!order || !orderId || !selectedChainId) return;
      
      const selectedChain = availableChains.find(c => c.id === selectedChainId);
      if (!selectedChain) return;

      try {
          setSaving(true);
          
          const payload = prepareGenerationalArtForOrder({
              selectedChain,
              artTitle: selectedChain.name, // Default title to chain name
              layoutType: order.items[0]?.productId?.includes('horizontal') ? 'horizontal' : 'circular',
              numberOfGenerations: selectedChain.generationCount, // Or order.maxGenerations
              submittedAt: new Date().toISOString()
          });

          // Update Firestore
          const orderRef = doc(db, "orders", orderId);
          await updateDoc(orderRef, {
             "formData.Generational": payload,
             "formStatus.Generational": true,
             lastFormUpdate: serverTimestamp()
          });
          
          // Trigger Status Update Check
          await checkAndUpdateOrderProgress(orderId, { Generational: true });
          
          // Navigate back
          navigate(`/account/orders/${orderId}`);
          
      } catch (err) {
          console.error("Failed to save configuration:", err);
          alert("Failed to save configuration. Please try again.");
      } finally {
          setSaving(false);
      }
  };

  const selectedChain = availableChains.find(c => c.id === selectedChainId);

  if (loading) {
      return (
          <Box sx={{ minHeight: "100vh", display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress />
          </Box>
      );
  }

  if (!order) {
      return <Box sx={{ p: 4 }}>Order not found.</Box>;
  }

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", bgcolor: "#f5f5f5" }}>
      {/* Studio Header */}
      <Box sx={{ 
        height: "160px", // Slight increase to ensure comfort, but user said "don't increase box size" meaning broadly. 120px is very tight for title+back button. 160px is standard hero. 
                       // Check user constraint: "it has plenty of space if the text was just centered" -> okay, let's try 140px and center properly.
        backgroundImage: `url('${BG_IMAGE}')`, 
        backgroundSize: "cover",
        backgroundPosition: "center",
        display: "flex", 
        flexDirection: "column",
        justifyContent: "center",
        boxShadow: "inset 0 0 0 2000px rgba(0,0,0,0.4)" // Add overlay to ensure text contrast
      }}>
        <Container maxWidth="xl">
            <Button 
              startIcon={<ArrowBack />} 
              sx={{ color: "white", mb: 1, opacity: 0.9, textTransform: 'none' }}
              onClick={() => navigate(orderId ? `/account/orders/${orderId}` : "/account")}
            >
              Back to Order
            </Button>
            <Typography variant="h4" component="h1" sx={{ fontFamily: '"Playfair Display", serif', color: 'white', fontWeight: 500 }}>
              Generational Art Studio
            </Typography>
        </Container>
      </Box>

      {/* Main Content - Dashboard Style */}
      <Container maxWidth="xl" sx={{ mt: 4, mb: 8, flexGrow: 1 }}>
           
           {!familyTreeData || familyTreeData.length === 0 ? (
               <Alert severity="warning" sx={{ mb: 3 }}>
                   <Typography variant="subtitle1" fontWeight="bold">Family Tree Data Missing</Typography>
                   <Typography variant="body2" paragraph>
                       You do not have any family tree data yet. To create Generational Art, we need to know who your ancestors are.
                   </Typography>
                   <Button variant="outlined" color="warning" onClick={() => navigate(`/account/tree?orderId=${orderId}`)}>
                       Go to Family Tree Editor
                   </Button>
               </Alert>
           ) : (
               <>
                <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 4, maxWidth: '800px' }}>
                    Select an ancestral line from your family tree to feature in your {order.items[0]?.name}. 
                    Changes are previewed in real-time.
                </Typography>

                <Box sx={{ display: 'grid', gridTemplateColumns: { lg: '1fr 1fr' }, gap: 4 }}>
                    {/* Left Col: Selection */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {/* Selector Card */}
                        <Paper elevation={0} sx={{ p: 0, bgcolor: 'transparent', borderRadius: 2, overflow: 'hidden' }}>
                            <ChainSelectionPanel 
                                order={order}
                                availableChains={availableChains}
                                selectedChainId={selectedChainId}
                                onChainSelection={setSelectedChainId}
                            />
                        </Paper>
                        
                        {/* Actions Card (moved here for logical flow) */}
                         <Paper elevation={2} sx={{ p: 3, borderRadius: 2, bgcolor: 'white' }}>
                            <OrderConfigurationActions 
                                order={order}
                                selectedChain={selectedChain}
                                saving={saving}
                                onSave={handleSave}
                            />
                        </Paper>
                    </Box>

                    {/* Right Col: Preview */}
                    <Box>
                         <Paper elevation={2} sx={{ p: 0, borderRadius: 2, overflow: 'hidden', height: '100%', minHeight: '400px', bgcolor: 'white' }}>
                             {selectedChain ? (
                                 <ChainPreviewPanel 
                                     selectedChain={selectedChain}
                                     order={order}
                                     isCompleted={false}
                                 />
                             ) : (
                                 <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4, color: 'text.secondary', bgcolor: '#fafafa' }}>
                                     <Typography variant="h6" color="text.disabled" gutterBottom>Preview Area</Typography>
                                     <Typography variant="body2">Select a family line to see a preview of your art.</Typography>
                                 </Box>
                             )}
                         </Paper>
                    </Box>
                </Box>
               </>
           )}

      </Container>
    </Box>
  );
};

export default GenerationalStudioPage;
